import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Toolbar, AppBar } from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  layoutEditAtom,
  selectedCustomerIdAtom,
  selectedLeadIdAtom,
} from '../../Atoms';
import LayoutEditBar from '../engine/layout/LayoutEditBar';
import AccountMenu from './AccountMenu';
import {
  isNotificationDenied,
  isNotificationSupported,
} from '../../Helpers/notifications';
import Fab from './Fab';
import ThemeFab from '../Button/ThemeFab';
import { FullSizeCenteredFlexBox } from '../FlexBox';
import TargetProgressPaper from './TargetProgressPaper';
import CreditsPaper from './CreditsPaper';
import PrioritiesAlert from './PrioritiesAlert';
import RedemptionPaper from './RedemptionPaper';
import { FULLPAGE_ROUTES, resolveRouteKey } from '../../Router/routes';
import { flatRoutesAtom } from '../../Router/routeAtoms';
import useHasPermission from '../../Hooks/useHasPermission';

const classes = {
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
};

const Navbar = function Navbar({
  onLogout,
  remainingSMS = null,
  callTargets,
  priorities = [],
  userRedemptionData,
  routesTree = [],
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation().pathname;

  const flatRoutes = useAtomValue(flatRoutesAtom);
  // While a layout is being arranged the bar belongs to it: the page below is what is being
  // changed, so what would be a dialog's title bar is the application's own.
  const layoutEdit = useAtomValue(layoutEditAtom);
  const setSelectedLeadId = useSetAtom(selectedLeadIdAtom);
  const setSelectedCustomerId = useSetAtom(selectedCustomerIdAtom);
  // Each control is bound to the key of the call behind it rather than to a list of roles: the
  // alert shows the branch's priorities and the cog opens the page that reads the settings, and
  // those two endpoints enforce exactly these keys.
  const canViewPriorities = useHasPermission('activities.viewpriority');
  const canViewSettings = useHasPermission('settings.view');

  const handlePurchaseHistoryPage = () => {
    setSelectedCustomerId('');
    setSelectedLeadId('');
    navigate('/purchaseHistory');
  };

  const redemptionAverage =
    userRedemptionData?.totalPractices && userRedemptionData?.totalAnsweredNum
      ? (userRedemptionData?.totalPractices /
          userRedemptionData?.totalAnsweredNum) *
        100
      : 0;
  const userRedemption =
    userRedemptionData?.practices && userRedemptionData?.answeredNum
      ? (userRedemptionData?.practices / userRedemptionData?.answeredNum) * 100
      : 0;

  return (
    <AppBar
      sx={{
        display:
          (flatRoutes.find(
            (route) =>
              `/${route.target}` === location &&
              FULLPAGE_ROUTES.includes(resolveRouteKey(route))
          ) ||
            location === '/401') &&
          'none',
      }}
      position="relative"
      color="transparent"
      align="right"
      elevation={0}
    >
      <Toolbar sx={classes.toolbar}>
        {layoutEdit ? (
          <FullSizeCenteredFlexBox gap="12px" paddingTop="16px">
            <LayoutEditBar />
          </FullSizeCenteredFlexBox>
        ) : (
          <FullSizeCenteredFlexBox
            justifyContent="flex-end"
            gap="12px"
            paddingTop="16px"
          >
            {canViewPriorities && <PrioritiesAlert priorities={priorities} />}
            {userRedemptionData?.totalPractices > 0 && (
              <RedemptionPaper
                disabled={!userRedemptionData}
                redemptionReached={userRedemption >= redemptionAverage}
              />
            )}
            <TargetProgressPaper callTargets={callTargets} />
            <CreditsPaper
              remainingSMS={remainingSMS}
              handlePurchaseHistoryPage={handlePurchaseHistoryPage}
            />
            {isNotificationSupported() && isNotificationDenied() && (
              <Fab
                title={intl.messages.SYSTEM_NOTIFICATIONS_DISABLED}
                href="chrome://settings/content/notifications"
                size="large"
              >
                <FmdBadIcon />
              </Fab>
            )}

            <Fab title="HELP" href="https://docs.databeasy.it/" size="large">
              <HelpOutlineRoundedIcon />
            </Fab>
            {canViewSettings && (
              <Fab
                size="large"
                title={intl.messages.SETTINGS}
                onClick={() => {
                  navigate('/settings');
                }}
              >
                <TuneRoundedIcon />
              </Fab>
            )}

            <ThemeFab size="large" />

            <AccountMenu onLogout={onLogout} routesTree={routesTree} />
          </FullSizeCenteredFlexBox>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
